/* DEV-288 (2026-09-26): collection page behaviour for the approved design.
   - infinite feed: 12 products at a time from the next page (or from the ATC ranker's full list when it ranked the
     collection), ?page= kept in the address, "Showing X of Y", a theme card to a neighbouring category every 12
     products and "keep browsing" with a shelf at the end;
   - back from a product: pages before the one in the address are put back and the scroll position restored;
   - quick filter chips and the bottom bar open the theme's filter drawer at the right group; the drawer's apply button
     shows how many products the choice leaves;
   - grid / large view switch, remembered in this browser;
   - DEV-444 (2026-09-28): under "Showing X of Y" a "Show 12 products" button loads the next page on tap (Clarity: people
     tapped the counter, which always said "loading more"); "loading more" shows only while a page is on its way;
   - the chosen size is kept for the session (lm-category-bar adds it to collection links);
   - DEV-479 (2026-09-29): with a size filter on, the "<neighbour> for you" shelf shows only products that have that size
     in stock (the neighbour is fetched with the same sizes plus "in stock"; Shopify matches both on one variant), its
     see-all links keep the filter and count what it finds, and with nothing found the shelf is left out;
   - CRO-66 (F, 2026-10-16): room for the products still to come is kept under the grid, so the counter, the collection
     text and the footer do not jump when a page arrives (phone CLS on New In was 0.7-1.9 when scrolling to the end);
   - CRO-67 (F, 2026-10-20): back from a product finds the saved place again when the address gained tracking parameters. */
const lmCol = window.__lmCol || (window.__lmCol = {});

if (!lmCol.ready) {
  lmCol.ready = true;
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

  const SIZE_KEY = 'lm-size';
  const VIEW_KEY = 'lm-view';
  const esc = (value) => String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const store = (area, key, value) => {
    try {
      if (value === null) area.removeItem(key);
      else area.setItem(key, value);
    } catch (error) {}
  };
  const read = (area, key) => {
    try {
      return area.getItem(key);
    } catch (error) {
      return null;
    }
  };
  const END_EXTRA = 330;
  const navType = (performance.getEntriesByType && performance.getEntriesByType('navigation')[0] || {}).type || '';

  /* ---------- session size ---------- */
  let lastSizes = new URL(location.href).searchParams.getAll('filter.v.option.size');
  const syncSize = () => {
    const sizes = new URL(location.href).searchParams.getAll('filter.v.option.size');
    if (sizes.length) store(sessionStorage, SIZE_KEY, JSON.stringify(sizes));
    else if (lastSizes.length) store(sessionStorage, SIZE_KEY, null);
    lastSizes = sizes;
  };

  /* ---------- view switch ---------- */
  const big = () => document.documentElement.classList.contains('lm-view-big');
  const applyView = (scope) => {
    scope.querySelectorAll('[data-lm-view]').forEach((button) => button.setAttribute('aria-pressed', String((button.dataset.lmView === 'big') === big())));
    if (big() && window.innerWidth < 1000) {
      scope.querySelectorAll('.product-list .lm-track img').forEach((image) => {
        image.sizes = '(max-width: 699px) 100vw, 50vw';
      });
    }
  };

  /* ---------- drawer ---------- */
  const openDrawer = (target) => {
    const drawer = document.getElementById('facets-drawer');
    if (!drawer || typeof drawer.show !== 'function') return;
    drawer.show();
    /* DEV-376: Sort opens a short radio list that sorts on tap; other targets show the full filters form */
    drawer.classList.toggle('lm-sort-only', target === 'accordion-sort-by');
    if (!drawer._lmSortWatch) {
      drawer._lmSortWatch = true;
      new MutationObserver(() => { if (!drawer.hasAttribute('open')) drawer.classList.remove('lm-sort-only'); }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
    }
    if (!target || target === 'drawer') return;
    const group = drawer.querySelector('#' + CSS.escape(target));
    if (!group) return;
    if ('open' in group) group.open = true;
    setTimeout(() => group.scrollIntoView({ block: 'start', behavior: 'smooth' }), 350);
  };

  let countTimer;
  const updateApplyCount = (form, t) => {
    clearTimeout(countTimer);
    countTimer = setTimeout(async () => {
      const button = form.querySelector('.facets-drawer__floating-apply button');
      if (!button || typeof form._buildUrl !== 'function') return;
      try {
        const html = await (await fetch(form._buildUrl().toString())).text();
        const data = JSON.parse(new DOMParser().parseFromString(html, 'text/html').querySelector('[data-lm-feed]').textContent);
        button.textContent = t.show.replace('%N', data.total);
      } catch (error) {}
    }, 250);
  };

  /* ---------- counts in the bottom bar ---------- */
  const setCartCount = (count) => {
    document.querySelectorAll('[data-lm-cart-count]').forEach((el) => {
      el.textContent = '(' + (count || 0) + ')';
    });
  };
  const setSavedCount = () => {
    let count = 0;
    try {
      const list = JSON.parse(localStorage.getItem('lm-atranka'));
      count = Array.isArray(list) ? list.length : 0;
    } catch (error) {}
    /* DEV-382: an empty wishlist shows no red "0" badge (same as the header) */
    document.querySelectorAll('[data-lm-saved-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-on', count > 0);
    });
  };
  document.addEventListener('cart:change', (event) => setCartCount(event.detail && event.detail.cart ? event.detail.cart.item_count : 0));
  document.addEventListener('lm:atranka', setSavedCount);

  /* ---------- feed ---------- */
  const feeds = new WeakMap();

  const rankerWillRun = (ranker) => {
    if (!ranker || ranker.dataset.complete || ranker.dataset.fallback) return false;
    if (new URL(location.href).searchParams.has('sort_by')) return false;
    try {
      const ranking = JSON.parse(ranker.querySelector('[data-atc-ranking]').textContent);
      const age = Date.now() - Date.parse(ranking.generatedAt);
      return Number.isFinite(age) && age > -300000 && age < 172800000 && ranking.handles.length > 0;
    } catch (error) {
      return false;
    }
  };

  const rankerReady = (ranker) => new Promise((resolve) => {
    if (!rankerWillRun(ranker)) return resolve();
    const timer = setTimeout(resolve, 8000);
    ranker.addEventListener('lm:ranked', () => {
      clearTimeout(timer);
      resolve();
    }, { once: true });
  });

  /* CRO-67 (F, 2026-10-20): the key is the path plus the filters and the sort only. Coming back from a product, Shopify's
     Shop sign-in check (302 via shop.app/accounts/sur) adds _su_rec to the address, and ads add utm_* / fbclid, so a key
     with the whole query never matched and the visitor landed at the top with the earlier pages gone. */
  const feedKey = () => {
    const url = new URL(location.href);
    const kept = Array.from(url.searchParams).filter(([key]) => key.indexOf('filter.') === 0 || key === 'sort_by');
    kept.sort((x, y) => (x[0] + '=' + x[1] < y[0] + '=' + y[1] ? -1 : 1));
    return 'lm-feed:' + url.pathname + (kept.length ? '?' + new URLSearchParams(kept).toString() : '');
  };

  /* CRO-67 v2: the theme head guard still looks up the old whole-query key. When tracking was added on return it
     misses the saved feed and leaves the footer visible during asynchronous prepending. Apply the existing restore
     visibility guard with the same canonical key as the feed, before waiting for the ranker or earlier pages. */
  if ((navType === 'back_forward' || navType === 'reload') &&
      (+new URL(location.href).searchParams.get('page') || 1) > 1 && read(sessionStorage, feedKey())) {
    document.documentElement.classList.add('lm-restoring');
    setTimeout(() => document.documentElement.classList.remove('lm-restoring'), 6000);
  }

  const initFeed = (section) => {
    const dataEl = section.querySelector('[data-lm-feed]');
    if (!dataEl || feeds.has(dataEl)) return;
    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }
    const old = feeds.get(section);
    if (old) old.stop();

    const t = data.t;
    const grid = section.querySelector('product-list.product-list');
    const sentinel = section.querySelector('.lm-feed-sentinel');
    const progress = section.querySelector('.lm-feed-progress');
    const ranker = section.querySelector('lm-atc-ranker');
    const per = data.perPage || 12;
    let first = data.page;
    let last = data.page;
    let loading = false;
    let stopped = false;
    let observer;
    let lmCheck = () => {};

    applyView(section);
    setSavedCount();
    syncSize();

    const drawerForm = document.querySelector('#facets-drawer form[is=facet-form]');
    if (drawerForm && !drawerForm.__lmCount) {
      drawerForm.__lmCount = true;
      drawerForm.addEventListener('change', () => updateApplyCount(drawerForm, t));
    }

    if (!grid || !sentinel) return;
    document.documentElement.classList.add('lm-feed-on');

    /* CRO-66: an empty block between the loading point and the counter is as tall as the products still to come plus
       the end block. A page that arrives takes its height out of the block, so nothing under it moves, even when the
       visitor has scrolled past the loaded products. The estimate is corrected only while the block is off screen. */
    const gap = document.createElement('div');
    gap.className = 'lm-feed-gap';
    gap.setAttribute('aria-hidden', 'true');
    sentinel.after(gap);
    let gapWant = 0;
    const gapNow = () => parseFloat(gap.style.height) || 0;
    const setGap = (height) => {
      gap.style.height = Math.max(0, Math.round(height)) + 'px';
    };
    const absorb = (grown) => {
      if (grown) setGap(gapNow() - grown);
    };
    const shownCount = () => grid.querySelectorAll(':scope > product-card, :scope > .lm-spb').length;
    const endGuess = () => {
      if (!(data.siblings || []).length) return 0;
      const share = window.innerWidth >= 1000 ? 0.19 : window.innerWidth >= 700 ? 0.24 : 0.58;
      return Math.round(grid.clientWidth * share * 4 / 3 + 130) + END_EXTRA;
    };
    /* rows still to come at the measured row pitch, a neighbour card after every page but the last, then the end block */
    const planGap = () => {
      const shown = shownCount();
      const left = last >= data.pages ? 0 : Math.max(0, data.total - shown);
      const list = cards();
      if (!left || !list.length) {
        gapWant = 0;
        return;
      }
      const tops = Array.from(new Set(list.map((card) => card.offsetTop))).sort((x, y) => x - y);
      const cols = Math.max(1, ...tops.map((rowTop) => list.filter((card) => card.offsetTop === rowTop).length));
      /* the usual step between product rows (the median: a row with a neighbour card or the sponsored card in it is taller) */
      const steps = tops.slice(1).map((rowTop, i) => rowTop - tops[i]).sort((x, y) => x - y);
      const pitch = steps.length ? steps[Math.floor((steps.length - 1) / 2)] : list[0].offsetHeight;
      const theme = grid.querySelector(':scope > .lm-theme-card');
      const themePitch = theme ? theme.offsetHeight + Math.max(0, pitch - list[0].offsetHeight) : 0;
      const rows = Math.ceil((shown + left) / cols) - Math.ceil(shown / cols);
      gapWant = rows * pitch + Math.max(0, data.pages - last - 1) * themePitch + endGuess();
    };
    /* below the screen the block is simply resized; when it starts above the screen the scroll position moves with it
       (set before shrinking, so the browser does not clamp it at the page end); while its top is on screen only the
       part under the screen is taken away */
    const settleGap = () => {
      const now = gapNow();
      if (Math.abs(now - gapWant) < 2) return;
      const root = document.documentElement;
      const box = gap.getBoundingClientRect();
      if (box.top >= window.innerHeight) {
        setGap(gapWant);
      } else if (box.top <= 0 && !gapWant) {
        /* only the room left over after the end (a filter changed the list): the page is scrolled up by the same amount,
           never more than the page has under the screen (a page that gets shorter under a visitor at its end moves) */
        const y = window.scrollY;
        const change = Math.max(gapWant - now, -y, -(root.scrollHeight - window.innerHeight - y));
        if (change > -2) return;
        root.style.overflowAnchor = 'none';
        if (change < 0) window.scrollTo(0, y + change);
        setGap(now + change);
        if (change > 0) window.scrollTo(0, y + change);
        requestAnimationFrame(() => { root.style.overflowAnchor = ''; });
      } else if (gapWant < now && box.bottom > window.innerHeight) {
        setGap(now - Math.min(now - gapWant, box.bottom - window.innerHeight));
      }
    };
    let heldEnd = null;
    const onGapScroll = () => {
      if (Math.abs(gapNow() - gapWant) >= 2) settleGap();
      if (heldEnd && heldEnd.getBoundingClientRect().top >= window.innerHeight) {
        heldEnd.style.minHeight = '';
        heldEnd = null;
      }
    };
    window.addEventListener('scroll', onGapScroll, { passive: true });

    const cards = () => Array.from(grid.children).filter((el) => el.tagName === 'PRODUCT-CARD');
    const handles = () => new Set(cards().map((card) => card.getAttribute('handle')));

    const themeCard = (index) => {
      const list = data.siblings || [];
      if (!list.length) return null;
      const sib = list[index % list.length];
      const link = document.createElement('a');
      link.className = 'lm-theme-card';
      link.href = sib.url;
      link.innerHTML = (sib.image ? '<img src="' + esc(sib.image) + '" alt="" width="64" height="80" loading="lazy">' : '') +
        '<div><b>' + esc(sib.title) + ' →</b><span>' + (sib.tag ? esc(sib.tag) + ' · ' : '') + sib.count + ' ' + esc(t.items) + '</span></div>';
      return link;
    };

    const showProgress = () => {
      if (!progress || data.pages < 2) return;
      /* CRO-40 (B, 2026-10-15): the sponsored product (.lm-spb) is one of the collection's products too, so the count
         matches the heading (Dresses: heading 66, bottom said "65 of 65") */
      const shown = cards().length + grid.querySelectorAll(':scope > .lm-spb').length;
      const done = first === 1 && last >= data.pages;
      const total = done ? shown : Math.max(shown, data.total);
      progress.hidden = false;
      const left = Math.min(per, total - shown);
      progress.innerHTML = esc(t.showing.replace('%X', shown).replace('%Y', total)) + (done || !loading ? '' : ' · ' + esc(t.loading)) +
        '<i><b style="width:' + Math.min(100, Math.round((shown / Math.max(1, total)) * 100)) + '%"></b></i>' +
        (done || left < 1 || !t.show ? '' : '<button type="button" class="lm-feed-more"' + (loading ? ' disabled style="visibility:hidden"' : '') + '>' + esc(t.show.replace('%N', left)) + '</button>');
    };

    const clean = (card) => {
      const clone = document.importNode(card, true);
      clone.removeAttribute('reveal-js');
      return clone;
    };
    const pageDoc = async (n) => {
      const url = new URL(location.href);
      url.searchParams.set('page', n);
      url.searchParams.set('section_id', data.section);
      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok) throw new Error(response.status);
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    };
    /* CRO-67: page 1 also has the sponsored block (a product too, left out of the ranked list); without it, coming back
       to a later page lost it */
    const pageNodes = async (n) => {
      if (ranker && ranker.ordered) {
        const nodes = ranker.ordered.slice((n - 1) * per, n * per).map(clean);
        if (n === 1) {
          try {
            nodes.push(...Array.from((await pageDoc(1)).querySelectorAll('product-list.product-list > [data-lm-sponsored]')).map(clean));
          } catch (error) {}
        }
        return nodes;
      }
      return Array.from((await pageDoc(n)).querySelectorAll('product-list.product-list > product-card, product-list.product-list > [data-lm-sponsored]')).map(clean);
    };

    const fresh = (nodes) => {
      const seen = handles();
      return nodes.filter((node) => !seen.has(node.getAttribute('handle')));
    };

    const setPageInUrl = () => {
      const url = new URL(location.href);
      if (last > 1) url.searchParams.set('page', last);
      else url.searchParams.delete('page');
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
    };

    const appendPage = async () => {
      if (loading || stopped || last >= data.pages) return;
      loading = true;
      showProgress();
      try {
        await rankerReady(ranker);
        if (last + 1 >= data.pages) loadShelf();
        const nodes = fresh(await pageNodes(last + 1));
        if (stopped) return;
        const before = grid.getBoundingClientRect().height;
        grid.append(...nodes);
        last += 1;
        const card = nodes.length >= per && last < data.pages ? themeCard(last - 1) : null;
        if (card) grid.append(card);
        absorb(grid.getBoundingClientRect().height - before);
        planGap();
        /* after the last page the end block goes into the room first (finish) */
        if (last < data.pages) settleGap();
        setPageInUrl();
        applyView(section);
        showProgress();
        if (last >= data.pages) finish();
      } catch (error) {
        observer && observer.disconnect();
        gapWant = 0;
        setGap(0);
      } finally {
        loading = false;
        if (!ended) showProgress();
      }
      if (last < data.pages) lmCheck();
    };

    /* the grid stays hidden while earlier pages go in above it, so nothing visible moves (CLS) */
    const prependPages = async () => {
      /* the browser's own scroll anchoring would move the page a second time, so it is off while this runs */
      const root = document.documentElement;
      grid.style.visibility = 'hidden';
      root.style.overflowAnchor = 'none';
      try { await prependLoop(); } finally { grid.style.visibility = ''; requestAnimationFrame(() => { root.style.overflowAnchor = ''; }); }
    };
    const prependLoop = async () => {
      while (first > 1 && !stopped) {
        const nodes = fresh(await pageNodes(first - 1));
        const anchor = grid.firstElementChild;
        const before = anchor ? anchor.getBoundingClientRect().top : 0;
        const card = themeCard(first - 2);
        if (card) grid.prepend(card);
        grid.prepend(...nodes);
        first -= 1;
        if (anchor) window.scrollBy(0, anchor.getBoundingClientRect().top - before);
      }
    };

    /* the "you may also like" shelf is fetched while the last page loads, so the end block goes in whole and at once;
       if it is still on its way, its height is kept so the footer does not move when it arrives (CLS) */
    let shelfReq = null;
    const shelfUrl = () => {
      const list = data.siblings || [];
      const url = new URL(list[0].url, location.href);
      const sizes = new URL(location.href).searchParams.getAll('filter.v.option.size');
      sizes.forEach((size) => url.searchParams.append('filter.v.option.size', size));
      if (sizes.length) url.searchParams.set('filter.v.availability', '1');
      return url;
    };
    const loadShelf = () => {
      if (shelfReq) return shelfReq;
      const list = data.siblings || [];
      if (!list.length) return (shelfReq = Promise.resolve(null));
      const url = shelfUrl();
      url.searchParams.set('section_id', data.section);
      shelfReq = fetch(url.toString(), { credentials: 'same-origin' })
        .then((response) => (response.ok ? response.text() : null))
        .then((html) => (html ? new DOMParser().parseFromString(html, 'text/html') : null))
        .catch(() => null);
      shelfReq.then((doc) => { shelfReq.doc = doc; shelfReq.done = true; });
      return shelfReq;
    };

    let ended = false;
    const finish = () => {
      if (ended) return;
      ended = true;
      sentinel.remove();
      gapWant = 0;
      /* with the visitor already in the empty block, the end block goes into it (where its top is on screen, right under
         the products), so the counter, the collection text and the footer stay where they are; the counter keeps its
         height when its button goes */
      const gapTop = gap.getBoundingClientRect().top;
      const gapOnScreen = gapNow() > 0 && gapTop < window.innerHeight;
      const placeEnd = () => {
        if (!gapOnScreen) (progress || grid).after(end);
        else if (gapTop > 0) gap.before(end);
        else gap.after(end);
      };
      const progressBefore = progress ? progress.getBoundingClientRect().height : 0;
      showProgress();
      if (progress && progress.getBoundingClientRect().top < window.innerHeight && progress.getBoundingClientRect().height < progressBefore) {
        progress.style.minHeight = progressBefore + 'px';
      }
      const progressGrown = () => (progress ? progress.getBoundingClientRect().height : 0) - progressBefore;
      const list = data.siblings || [];
      if (!list.length) {
        absorb(progressGrown());
        settleGap();
        return;
      }
      const end = document.createElement('div');
      end.className = 'lm-feed-end';
      /* v6 (2026-09-26): studio link, "keep browsing" chips (neighbours, New In, Sale), then the "<first neighbour> for you" shelf with a see-all card, as in the design */
      const more = data.more && data.more.length ? data.more : list;
      const sized = new URL(location.href).searchParams.has('filter.v.option.size');
      const allUrl = sized ? shelfUrl().pathname + shelfUrl().search : list[0].url;
      let allCount = list[0].count;
      end.innerHTML = (data.studio && t.studio ? '<a class="lm-studio-link" href="' + esc(data.studio) + '">' + esc(t.studio) + '</a>' : '') +
        '<h4>' + esc(t.more) + '</h4><div class="lm-feed-end__chips">' +
        more.map((sib) => '<a class="lm-chip" href="' + esc(sib.url) + '">' + esc(sib.title) + '</a>').join('') + '</div>' +
        '<div class="lm-feed-shelf"><div class="lm-feed-shelf__h"><h3>' + esc(t.for_you.replace('%C', list[0].title)) + '</h3>' +
        (t.all ? '<a href="' + esc(allUrl) + '">' + esc(t.all.replace('%N', allCount)) + '</a>' : '') + '</div><div class="lm-shelf"></div></div>';
      const shelf = end.querySelector('.lm-shelf');
      /* CRO-39 (B, 2026-10-15): a collection of 1-6 products with no filter on shows the shelf straight under its products,
         as a grid of the 8 cards, before the studio link and the chips (ads land on Mini / Maxi with 1-6 dresses) */
      const small = data.total <= 6 && first === 1 && !Array.from(new URL(location.href).searchParams.keys()).some((key) => key.indexOf('filter.') === 0);
      if (small) {
        end.classList.add('lm-feed-end--small');
        end.prepend(shelf.parentElement);
      }
      const fill = (doc) => {
        if (doc) {
          /* DEV-360: the neighbour's shelf skips what the grid already shows (every Midi dress is also in Dresses) */
          const seen = handles();
          if (sized) {
            try {
              allCount = JSON.parse(doc.querySelector('[data-lm-feed]').textContent).total;
              const head = end.querySelector('.lm-feed-shelf__h a');
              if (head && t.all) head.textContent = t.all.replace('%N', allCount);
            } catch (error) {}
          }
          Array.from(doc.querySelectorAll('product-list.product-list > product-card')).filter((card) => !seen.has(card.getAttribute('handle'))).slice(0, 8).forEach((card) => {
            const clone = document.importNode(card, true);
            clone.removeAttribute('reveal-js');
            clone.querySelectorAll('.lm-track img:not(:first-child), .lm-dots, .lm-qadd').forEach((el) => el.remove());
            shelf.append(clone);
          });
        }
        if (!shelf.children.length) { shelf.parentElement.remove(); return; }
        const all = document.createElement('a');
        all.className = 'lm-more-card';
        all.href = allUrl;
        all.innerHTML = esc(t.see_all) + '<br>' + esc(allCount) + ' ' + esc(t.items_acc || t.items) + ' →';
        shelf.append(all);
        if (small) shelf.style.minHeight = '';
      };
      const request = loadShelf();
      if (request.done) {
        fill(request.doc);
        placeEnd();
        absorb(progressGrown() + end.getBoundingClientRect().height);
        settleGap();
        return;
      }
      placeEnd();
      if (small) {
        const cols = window.innerWidth >= 1000 ? 4 : 2;
        shelf.style.minHeight = Math.round(Math.ceil(8 / cols) * ((shelf.clientWidth / cols) * 4 / 3 + 170)) + 'px';
      } else {
        const share = window.innerWidth >= 1000 ? 0.19 : window.innerWidth >= 700 ? 0.24 : 0.58;
        shelf.style.minHeight = Math.round(shelf.clientWidth * share * 4 / 3 + 130) + 'px';
      }
      absorb(progressGrown() + end.getBoundingClientRect().height);
      settleGap();
      request.then((doc) => {
        const endBefore = end.getBoundingClientRect().height;
        fill(doc);
        const grown = end.getBoundingClientRect().height - endBefore;
        if (grown > 0) absorb(grown);
        else if (grown < 0 && end.getBoundingClientRect().top < window.innerHeight) {
          /* the shelf came back empty while the end block is on screen: its room is kept until it is under the screen */
          end.style.minHeight = endBefore + 'px';
          heldEnd = end;
        }
        settleGap();
      });
    };

    const start = async () => {
      await rankerReady(ranker);
      if (stopped) return;
      if (cards().length >= per && last < data.pages) {
        const card = themeCard(last - 1);
        if (card) grid.append(card);
      }
      const saved = JSON.parse(read(sessionStorage, feedKey()) || 'null');
      if (first > 1) {
        if (saved && (navType === 'back_forward' || navType === 'reload')) {
          try {
            await prependPages();
          } catch (error) {}
        } else {
          /* a direct link to a later page: earlier pages come in only when asked, so the page never jumps by itself */
          const prev = document.createElement('button');
          prev.type = 'button';
          prev.className = 'lm-feed-prev';
          prev.textContent = t.prev || '';
          prev.addEventListener('click', async () => {
            /* removed right away, while the tap still counts as the reason for the move */
            const anchor = grid.firstElementChild;
            const before = anchor.getBoundingClientRect().top;
            prev.remove();
            window.scrollBy(0, anchor.getBoundingClientRect().top - before);
            try {
              await prependPages();
            } catch (error) {}
            showProgress();
          });
          grid.before(prev);
        }
      }
      planGap();
      setGap(gapWant);
      if (saved && (navType === 'back_forward' || navType === 'reload') && saved.last === last) {
        window.scrollTo(0, saved.y);
      }
      document.documentElement.classList.remove('lm-restoring');
      showProgress();
      if (last >= data.pages) {
        finish();
        return;
      }
      /* a scroll check rather than an IntersectionObserver alone: a fast fling can jump past the sentinel without it ever
         intersecting, so anything at or above 600 px under the screen counts */
      let frame = 0;
      const check = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          if (!stopped && sentinel.isConnected && sentinel.getBoundingClientRect().top < window.innerHeight + 600) appendPage();
        });
      };
      window.addEventListener('scroll', check, { passive: true });
      observer = { disconnect: () => window.removeEventListener('scroll', check) };
      lmCheck = check;
      check();
    };

    /* DEV-444: the button loads the next page (the scroll check still loads on its own); focus stays on the counter */
    if (progress) {
      progress.tabIndex = -1;
      progress.addEventListener('click', (event) => {
        if (!event.target.closest || !event.target.closest('.lm-feed-more')) return;
        progress.focus({ preventScroll: true });
        appendPage();
      });
    }

    const save = () => store(sessionStorage, feedKey(), JSON.stringify({ last, y: Math.round(window.scrollY) }));
    window.addEventListener('pagehide', save);
    section.addEventListener('click', (event) => {
      if (event.target.closest && event.target.closest('a[href*="/products/"]')) save();
    });

    feeds.set(dataEl, true);
    feeds.set(section, {
      stop: () => {
        stopped = true;
        observer && observer.disconnect();
        window.removeEventListener('scroll', onGapScroll);
        window.removeEventListener('pagehide', save);
      }
    });
    start();
  };

  /* DEV-376: choosing a sort option applies it straight away (no Apply step) */
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!input || input.name !== 'sort_by' || !input.closest('#facets-drawer')) return;
    const url = new URL(location.href);
    url.searchParams.set('sort_by', input.value);
    url.searchParams.delete('page');
    location.assign(url.toString());
  });

  /* AUD-1013-F01 (G, 2026-10-13): Enter in a filter field (price from / to) did nothing: the drawer's Apply button is
     type="button", so the form has no implicit submit. Enter now does what a tap on Apply does. */
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || event.isComposing) return;
    const input = event.target;
    if (!input || input.tagName !== 'INPUT' || !/^filter\./.test(input.name) || !/^(number|text)$/.test(input.type)) return;
    const drawer = input.closest('#facets-drawer');
    const apply = drawer && drawer.querySelector('button[is=facet-apply-button]');
    if (!apply) return;
    event.preventDefault();
    input.dispatchEvent(new Event('change', { bubbles: true }));
    apply.click();
  });

  /* ---------- clicks ---------- */
  document.addEventListener('click', (event) => {
    const target = event.target.closest ? event.target : null;
    if (!target) return;
    const open = target.closest('[data-lm-open]');
    if (open) {
      event.preventDefault();
      openDrawer(open.dataset.lmOpen);
      return;
    }
    const view = target.closest('[data-lm-view]');
    if (view) {
      document.documentElement.classList.toggle('lm-view-big', view.dataset.lmView === 'big');
      store(localStorage, VIEW_KEY, view.dataset.lmView);
      applyView(document);
      return;
    }
    const cart = target.closest('[data-lm-cart]');
    const drawer = document.getElementById('cart-drawer');
    if (cart && drawer && typeof drawer.show === 'function') {
      event.preventDefault();
      drawer.show();
    }
  });

  /* ---------- start and re-start after the theme reloads the section for a filter ---------- */
  const boot = () => {
    document.querySelectorAll('.shopify-section--main-collection').forEach((section) => {
      initFeed(section);
      if (!section.__lmWatch) {
        section.__lmWatch = true;
        new MutationObserver(() => initFeed(section)).observe(section, { childList: true });
      }
    });
    fetch((window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'cart.js')
      .then((response) => response.json())
      .then((cart) => setCartCount(cart.item_count))
      .catch(() => {});
  };
  boot();
}
